import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Music,
  Film,
  Download,
  X,
  Loader2,
  Play,
  Pause,
  Mic,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';  // Only for auth
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { VoiceNotePlayer } from './VoiceNotePlayer';
import { LocationPreview } from './LocationPreview';
import {
  getCachedAttachments,
  cacheAttachments,
} from '@/lib/attachment-cache';

interface Attachment {
  attachment_id: string;
  file_url: string;
  file_name: string;
  file_type: 'image' | 'pdf' | 'audio' | 'voice_note' | 'document' | 'video' | 'location';
  mime_type: string;
  file_size: number;
  thumbnail_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  metadata?: Record<string, any>;
  signed_url?: string;  // Pre-signed URL from edge function
  thumbnail_signed_url?: string;  // Pre-signed thumbnail URL from edge function
}

interface MessageAttachmentPreviewProps {
  messageId: string;
  attachments?: Attachment[];
  isOwnMessage?: boolean;
  onLoad?: () => void;
  className?: string;
  onMediaClick?: (mediaId: string) => void;
}

const getFileIcon = (type: Attachment['file_type']) => {
  switch (type) {
    case 'image':
      return <ImageIcon className="h-4 w-4" />;
    case 'audio':
    case 'voice_note':
      return <Music className="h-4 w-4" />;
    case 'video':
      return <Film className="h-4 w-4" />;
    case 'pdf':
      return <FileText className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDuration = (seconds?: number) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const MessageAttachmentPreview: React.FC<MessageAttachmentPreviewProps> = ({
  messageId,
  attachments: providedAttachments,
  isOwnMessage = false,
  onLoad,
  className,
  onMediaClick,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>(providedAttachments || []);
  const [loading, setLoading] = useState(!providedAttachments);

  // Update attachments when providedAttachments changes
  useEffect(() => {
    if (providedAttachments && providedAttachments.length > 0) {
      // Disable logging in production
      setAttachments(providedAttachments);
      setLoading(false);
    }
  }, [providedAttachments, messageId]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [audioPlaying, setAudioPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audioProgress, setAudioProgress] = useState<Record<string, { currentTime: number; duration: number }>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const progressIntervals = useRef<Record<string, NodeJS.Timeout>>({});

  // Fetch attachments if not provided
  useEffect(() => {
    if (!providedAttachments && messageId) {
      fetchAttachments();
    }
  }, [messageId, providedAttachments]);

  // Generate signed URLs for attachments
  useEffect(() => {
    if (attachments.length > 0) {
      // Always use the signed URLs from edge function
      const urls: Record<string, string> = {};
      attachments.forEach(att => {
        if (att.signed_url) {
          urls[att.attachment_id] = att.signed_url;
        }
        if (att.thumbnail_signed_url) {
          urls[`${att.attachment_id}_thumb`] = att.thumbnail_signed_url;
        }
      });


      setSignedUrls(urls);
    }
  }, [attachments]);

  const fetchAttachments = async () => {
    try {
      // Check cache first
      const cached = getCachedAttachments(messageId);
      if (cached) {
        setAttachments(cached);
        setLoading(false);
        onLoad?.();
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments?message_id=${messageId}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setAttachments(data);
          // Cache the fetched attachments
          cacheAttachments(messageId, data);
        } else {
          setAttachments([]);
        }
      } else {
        const errorText = await response.text();
        setError(`Failed to load attachments: ${response.status}`);
      }
    } catch (error) {
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
      onLoad?.();
    }
  };

  // Removed generateSignedUrls function - now using signed URLs from edge function

  const handleDownload = async (attachment: Attachment) => {
    const url = signedUrls[attachment.attachment_id];
    if (!url) return;

    try {
      // Fetch the file content
      const response = await fetch(url);
      const blob = await response.blob();

      // Create a blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Download failed
      // Fallback to opening in new tab
      window.open(url, '_blank');
    }
  };

  const toggleAudio = (attachmentId: string) => {
    const audio = audioRefs.current[attachmentId];
    if (!audio) return;

    if (audioPlaying === attachmentId) {
      audio.pause();
      setAudioPlaying(null);
      // Clear progress interval
      if (progressIntervals.current[attachmentId]) {
        clearInterval(progressIntervals.current[attachmentId]);
        delete progressIntervals.current[attachmentId];
      }
    } else {
      // Pause any other playing audio and clear their intervals
      Object.entries(audioRefs.current).forEach(([id, audioEl]) => {
        if (id !== attachmentId) {
          audioEl.pause();
          if (progressIntervals.current[id]) {
            clearInterval(progressIntervals.current[id]);
            delete progressIntervals.current[id];
          }
        }
      });

      audio.play();
      setAudioPlaying(attachmentId);

      // Start progress tracking
      progressIntervals.current[attachmentId] = setInterval(() => {
        setAudioProgress(prev => ({
          ...prev,
          [attachmentId]: {
            currentTime: audio.currentTime,
            duration: audio.duration || 0
          }
        }));
      }, 100);
    }
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(progressIntervals.current).forEach(interval => clearInterval(interval));
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs text-muted-foreground">Loading attachments...</span>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    if (error) {
      return (
        <div className="text-xs text-destructive mt-1">
          {error}
        </div>
      );
    }
    return null;
  }

  // Create stacked card layout for 2+ attachments
  const hasMultipleAttachments = attachments.length > 1;

  return (
    <>
      {hasMultipleAttachments ? (
        // Stacked card layout for multiple attachments
        <div className={cn('relative', className)}>
          {/* Container with proper height for stacking */}
          <div className="relative" style={{ minHeight: attachments.length > 3 ? '240px' : '220px' }}>
            {attachments.slice(0, 4).map((attachment, index) => {
              const url = signedUrls[attachment.attachment_id];
              const thumbUrl = signedUrls[`${attachment.attachment_id}_thumb`];
              const isLast = index === Math.min(3, attachments.length - 1);
              const remainingCount = attachments.length - 4;

              return (
                <div
                  key={attachment.attachment_id}
                  className={cn(
                    "absolute rounded-lg overflow-hidden border-2 shadow-lg cursor-pointer transition-all hover:z-50",
                    "bg-background",
                    index === 0 && "z-40 w-[200px] h-[180px]",
                    index === 1 && "z-30 w-[190px] h-[170px] top-3 left-3",
                    index === 2 && "z-20 w-[180px] h-[160px] top-6 left-6",
                    index === 3 && "z-10 w-[170px] h-[150px] top-9 left-9"
                  )}
                  style={{
                    transform: `rotate(${index * 2 - 2}deg)`,
                    boxShadow: `0 ${4 + index * 2}px ${8 + index * 4}px rgba(0,0,0,${0.1 + index * 0.05})`
                  }}
                  onClick={() => {
                    if (onMediaClick) {
                      onMediaClick(attachment.attachment_id);
                    } else if (attachment.file_type === 'image') {
                      setSelectedImage(url);
                    } else if (attachment.file_type === 'video') {
                      setSelectedVideo(url);
                    } else if (attachment.file_type === 'pdf') {
                      setSelectedPdf(url);
                    }
                  }}
                >
                  {/* Render based on file type */}
                  {attachment.file_type === 'image' ? (
                    <>
                      <img
                        src={thumbUrl || url}
                        alt={attachment.file_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </>
                  ) : attachment.file_type === 'video' ? (
                    <div className="relative w-full h-full bg-black">
                      <video
                        src={url}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="h-6 w-6 text-black ml-0.5" />
                        </div>
                      </div>
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                  ) : attachment.file_type === 'pdf' ? (
                    <div className="relative w-full h-full bg-white flex flex-col">
                      {/* Red PDF stripe at top */}
                      <div className="absolute top-0 left-0 right-0 h-8 bg-red-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold tracking-wider">PDF</span>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center p-4 pt-10">
                        <FileText className="h-16 w-16 text-red-500 mb-2" />
                        <p className="text-xs font-medium text-gray-700 text-center truncate max-w-full">
                          {attachment.file_name}
                        </p>
                      </div>
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                  ) : attachment.file_type === 'audio' || attachment.file_type === 'voice_note' ? (
                    <div className="relative w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center p-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-2">
                        <Music className="h-12 w-12 text-white" />
                      </div>
                      <p className="text-xs font-medium text-white text-center truncate max-w-full">
                        {attachment.file_type === 'voice_note' ? 'Voice Note' : attachment.file_name}
                      </p>
                      {attachment.duration && (
                        <span className="text-xs text-white/80 mt-1">{formatDuration(attachment.duration)}</span>
                      )}
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                  ) : attachment.file_type === 'location' ? (
                    <div className="relative w-full h-full bg-gradient-to-br from-emerald-500 to-teal-500 flex flex-col items-center justify-center p-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-2">
                        <MapPin className="h-12 w-12 text-white" />
                      </div>
                      <p className="text-xs font-medium text-white text-center truncate max-w-full">
                        {attachment.metadata?.place_name || 'Shared Location'}
                      </p>
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full bg-muted flex flex-col items-center justify-center p-3">
                      <div className="h-12 w-12 flex items-center justify-center bg-background/80 rounded-lg mb-2">
                        {getFileIcon(attachment.file_type)}
                      </div>
                      <p className="text-xs font-medium text-center truncate max-w-full px-1">
                        {attachment.file_name || 'Document'}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {formatFileSize(attachment.file_size)}
                      </p>
                      {isLast && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hover overlay with actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(attachment);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Count badge for total attachments */}
          {attachments.length > 1 && (
            <div className="absolute -top-2 -right-2 z-50 bg-primary text-primary-foreground rounded-full px-2 py-1 text-xs font-bold shadow-md">
              {attachments.length} files
            </div>
          )}
        </div>
      ) : (
        // Original single attachment layout
        <div className={cn('grid grid-cols-1 gap-2', className)}>
          {attachments.map((attachment) => {
            const url = signedUrls[attachment.attachment_id];
            const thumbUrl = signedUrls[`${attachment.attachment_id}_thumb`];

            if (attachment.file_type === 'image') {
              return (
                <div
                  key={attachment.attachment_id}
                  className="relative group cursor-pointer block"
                  onClick={() => {
                    if (onMediaClick) {
                      onMediaClick(attachment.attachment_id);
                    } else {
                      setSelectedImage(url);
                    }
                  }}
                >
                  <img
                    src={thumbUrl || url}
                    alt={attachment.file_name}
                    className="w-full h-[200px] object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(url);
                      }}
                    >
                      <ImageIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(attachment);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            }

            // Video preview
          if (attachment.file_type === 'video') {
            return (
              <div
                key={attachment.attachment_id}
                className="relative group cursor-pointer block"
                onClick={() => {
                  if (onMediaClick) {
                    onMediaClick(attachment.attachment_id);
                  } else {
                    setSelectedVideo(url);
                  }
                }}
              >
                <div className="relative w-full h-[200px] bg-black">
                  <video
                    src={url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="h-6 w-6 text-black ml-0.5" />
                    </div>
                  </div>
                  {attachment.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded text-xs">
                      {formatDuration(attachment.duration)}
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVideo(url);
                    }}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(attachment);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          }

          // Location attachments
          if (attachment.file_type === 'location') {
            const metadata = attachment.metadata || {};
            return (
              <LocationPreview
                key={attachment.attachment_id}
                latitude={metadata.latitude}
                longitude={metadata.longitude}
                address={metadata.address}
                placeName={metadata.place_name || attachment.file_name}
                staticMapUrl={metadata.static_map_url || attachment.file_url}
                isOwnMessage={isOwnMessage}
              />
            );
          }

          // Voice notes - use the dedicated VoiceNotePlayer component
          if (attachment.file_type === 'voice_note') {
            return (
              <VoiceNotePlayer
                key={attachment.attachment_id}
                url={url}
                duration={attachment.duration || 0}
                waveform={attachment.metadata?.waveform as number[] | undefined}
                isOwn={isOwnMessage}
                className="w-full"
              />
            );
          }

          // Regular audio files
          if (attachment.file_type === 'audio') {
            const isPlaying = audioPlaying === attachment.attachment_id;
            const progress = audioProgress[attachment.attachment_id] || { currentTime: 0, duration: 0 };
            const progressPercentage = progress.duration > 0 ? (progress.currentTime / progress.duration) * 100 : 0;

            return (
              <div
                key={attachment.attachment_id}
                className="w-full bg-background/50 p-3 space-y-2 cursor-pointer group rounded-lg"
                onClick={() => {
                  if (onMediaClick) {
                    onMediaClick(attachment.attachment_id);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAudio(attachment.attachment_id);
                    }}
                    className="h-10 w-10 rounded-full bg-primary/10 hover:bg-primary/20 flex-shrink-0"
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </Button>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Music className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs font-medium">
                          {attachment.file_name || 'Audio'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(attachment);
                        }}
                        className="h-6 w-6"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Custom progress bar */}
                    <div className="space-y-1">
                      <div
                        className="relative h-1.5 bg-muted rounded-full overflow-hidden cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          const audio = audioRefs.current[attachment.attachment_id];
                          if (!audio) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const percentage = x / rect.width;
                          audio.currentTime = percentage * (audio.duration || 0);
                        }}
                      >
                        <div
                          className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-100"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">
                          {isPlaying || progress.currentTime > 0
                            ? formatDuration(Math.floor(progress.currentTime))
                            : '0:00'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {progress.duration > 0
                            ? formatDuration(Math.floor(progress.duration))
                            : formatDuration(attachment.duration) || '--:--'}
                        </span>
                      </div>
                    </div>

                    {/* Hidden audio element */}
                    <audio
                      ref={(el) => {
                        if (el) {
                          audioRefs.current[attachment.attachment_id] = el;
                          // Set initial duration when loaded
                          el.addEventListener('loadedmetadata', () => {
                            setAudioProgress(prev => ({
                              ...prev,
                              [attachment.attachment_id]: {
                                currentTime: 0,
                                duration: el.duration
                              }
                            }));
                          });
                        }
                      }}
                      src={url}
                      onEnded={() => {
                        setAudioPlaying(null);
                        if (progressIntervals.current[attachment.attachment_id]) {
                          clearInterval(progressIntervals.current[attachment.attachment_id]);
                          delete progressIntervals.current[attachment.attachment_id];
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            );
          }

          // PDF and other documents
          if (attachment.file_type === 'pdf') {
            return (
              <div
                key={attachment.attachment_id}
                className="relative group cursor-pointer block"
                onClick={() => {
                  if (onMediaClick) {
                    onMediaClick(attachment.attachment_id);
                  } else {
                    setSelectedPdf(url);
                  }
                }}
              >
                <div className="w-full h-[200px] bg-muted flex flex-col items-center justify-center">
                  <div className="h-20 w-20 flex items-center justify-center bg-background/80 rounded-lg mb-3">
                    <FileText className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium px-2 text-center truncate max-w-full">
                    {attachment.file_name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF • {formatFileSize(attachment.file_size)}
                  </p>
                </div>
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onMediaClick) {
                        onMediaClick(attachment.attachment_id);
                      } else {
                        setSelectedPdf(url);
                      }
                    }}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(attachment);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          }

          // Other documents (fallback)
          return (
            <div
              key={attachment.attachment_id}
              className={cn(
                'flex items-center gap-2 p-3 rounded-lg border',
                isOwnMessage ? 'bg-primary/10' : 'bg-muted'
              )}
            >
              <div className="h-10 w-10 flex items-center justify-center bg-background rounded">
                {getFileIcon(attachment.file_type)}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(attachment.file_size)}
                </p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDownload(attachment)}
                className="h-8 w-8"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
      )}

      {/* Sleek Image preview modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[85vh] p-0 overflow-hidden bg-black/95 border-0">
          {/* Compact floating buttons */}
          <div className="absolute top-3 right-3 z-10 flex gap-1.5">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => {
                const attachment = attachments.find(
                  (a) => signedUrls[a.attachment_id] === selectedImage
                );
                if (attachment) handleDownload(attachment);
              }}
              className="h-8 w-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-0"
              title="Download"
            >
              <Download className="h-4 w-4 text-white" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setSelectedImage(null)}
              className="h-8 w-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-0"
              title="Close"
            >
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>
          {selectedImage && (
            <div className="flex items-center justify-center p-2 sm:p-4">
              <img
                src={selectedImage}
                alt="Preview"
                className="max-w-full max-h-[80vh] object-contain rounded"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sleek PDF viewer modal */}
      <Dialog open={!!selectedPdf} onOpenChange={() => setSelectedPdf(null)}>
        <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[85vh] p-0 overflow-hidden">
          {/* Compact header */}
          <div className="absolute top-3 right-3 z-10 flex gap-1.5">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => {
                const attachment = attachments.find(
                  (a) => signedUrls[a.attachment_id] === selectedPdf
                );
                if (attachment) handleDownload(attachment);
              }}
              className="h-8 w-8 bg-background/80 hover:bg-background/90 backdrop-blur-sm"
              title="Download PDF"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setSelectedPdf(null)}
              className="h-8 w-8 bg-background/80 hover:bg-background/90 backdrop-blur-sm"
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {selectedPdf && (
            <iframe
              src={selectedPdf}
              className="w-full h-full"
              title="PDF Viewer"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Sleek Video player modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[85vh] p-0 overflow-hidden bg-black border-0">
          {/* Compact floating controls */}
          <div className="absolute top-3 right-3 z-10 flex gap-1.5">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => {
                const attachment = attachments.find(
                  (a) => signedUrls[a.attachment_id] === selectedVideo
                );
                if (attachment) handleDownload(attachment);
              }}
              className="h-8 w-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-0"
              title="Download Video"
            >
              <Download className="h-4 w-4 text-white" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setSelectedVideo(null)}
              className="h-8 w-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-0"
              title="Close"
            >
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>
          {selectedVideo && (
            <div className="flex items-center justify-center h-full">
              <video
                src={selectedVideo}
                controls
                autoPlay
                className="max-w-full max-h-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};